import React from 'react';
import { Route, Redirect, RouteProps } from 'react-router-dom';
import { useAppSelector } from 'app/config/store';
import ErrorBoundary from 'app/shared/error/error-boundary';
import { AUTHORITIES } from 'app/config/constants';
import { ICompany } from '../model/company.model';

interface IOwnProps extends RouteProps {
  hasAnyAuthorities?: string[];
  forbiddenAuthorities?: string[];
  isTemporaryUnblock?: boolean | null;
  RouteProps?: any;
}

export const PrivateRouteComponent = ({ component: Component, hasAnyAuthorities = [], forbiddenAuthorities = [], isTemporaryUnblock, ...rest }: IOwnProps) => {
  const isAuthenticated = useAppSelector(state => state.authentication.isAuthenticated);
  const sessionHasBeenFetched = useAppSelector(state => state.authentication.sessionHasBeenFetched);
  const account = useAppSelector(state => state.authentication.account);
  const isAuthorized = hasAnyAuthority(account.authorities, hasAnyAuthorities);
  const isForbidden = hasAnyForbiddenAuthority(account.authorities, forbiddenAuthorities);

  const checkAuthorities = props =>
    (isAuthorized && !isForbidden) || isTemporaryUnblock ? (
      <ErrorBoundary>
        <Component {...props} />
      </ErrorBoundary>
    ) : (
      <Redirect to="/login" />
    );

  const renderRedirect = props => {
    if (!sessionHasBeenFetched) {
      return <div></div>;
    } else {
      return isAuthenticated ? (
        checkAuthorities(props)
      ) : (
        <Redirect
          to={{
            pathname: '/login',
            state: { from: props.location },
          }}
        />
      );
    }
  };

  if (!Component) throw new Error(`A component needs to be specified for private route for path ${(rest as any).path}`);

  return <Route {...rest} render={renderRedirect} />;
};

export const hasAnyAuthority = (authorities: string[], hasAnyAuthorities: string[]) => {
  if (authorities && authorities.length !== 0) {
    if (hasAnyAuthorities.length === 0) {
      return true;
    }
    return hasAnyAuthorities.some(auth => authorities.includes(auth));
  }
  return false;
};

export const hasAnyForbiddenAuthority = (authorities: string[], forbiddenAuthorities: string[]) => {
  if (authorities && authorities.length !== 0) {
    return forbiddenAuthorities.some(forbiddenAuth => authorities.includes(forbiddenAuth));
  }
  return false;
};

/**
 * Checks if the user has authorization for maintenance based on the company ID.
 * @param authorities The user's authorities.
 * @param company The company for which authorization is being checked.
 * @returns True if the user is authorized for maintenance for the current company, false otherwise.
 */
export const isUserAuthorizedForMaintenanceByCompany = (authorities: string[], company: ICompany) => {
  // IDs of companies for which the user is authorized for maintenance
  const authorizedCompanyIds = [2, 2810501, 26486601, 39039051, 195300851]; // Senai, Librelato Leves, Librelato Logística, Librelato Pesados, Librelato P&D

  // Authorities for maintenance
  const maintenanceAuthorities = [AUTHORITIES.TRANSPORTER_ADMIN, AUTHORITIES.MANUFACTURER_ADMIN];

  // Check if the current company is in the list of authorized companies
  if (authorizedCompanyIds.includes(company?.id)) {
    // Check if the user has maintenance authorization
    return maintenanceAuthorities.some(auth => authorities.includes(auth));
  }

  return false; // Return false if the company is not authorized
};

/**
 * Checks if the user has authorization for Device Block based on the company ID.
 * @param authorities The user's authorities.
 * @param company The company for which authorization is being checked.
 * @returns True if the user is authorized for maintenance for the current company, false otherwise.
 */
export const isUserAuthorizedForDeviceBlockByCompany = (authorities: string[], company: ICompany) => {
  // IDs of companies for which the user is authorized for device block
  const authorizedCompanyIds = [1483470068]; // DELOG SERVIÇOS E LOGÍSTICAS LTDA

  // Authorities for device block
  const deviceBlockeAuthorities = [AUTHORITIES.TRANSPORTER_ADMIN, AUTHORITIES.MANUFACTURER_ADMIN];

  // Check if the current company is in the list of authorized companies
  if (authorizedCompanyIds.includes(company?.id)) {
    // Check if the user has device block authorization
    return deviceBlockeAuthorities.some(auth => authorities.includes(auth));
  }

  return false; // Return false if the company is not authorized
};

/**
 * Tires Authorized Companies
 */
export const tireAuthorizedCompanyIds = [2, 2810501, 39039051, 17018191601];

export const isTireAuthorizedByCompany = (companyId?: number) => companyId !== undefined && tireAuthorizedCompanyIds.includes(companyId);

/**
 * A route wrapped in an authentication check so that routing happens only when you are authenticated.
 * Accepts same props as React router Route.
 * The route also checks for authorization if hasAnyAuthorities is specified.
 */
export default PrivateRouteComponent;
